import React, { useState } from 'react';
import { X, Loader2, Play, Pause, Cloud } from 'lucide-react';
import services from '../../../API';

const SoundEffectModal = ({ isOpen, onClose, onGenerate, onSelectTrack, onAddFromDrive, videoAdId }) => {
    const [prompt, setPrompt] = useState('');
    const [enhanceWithAi, setEnhanceWithAi] = useState(true);
    const [duration, setDuration] = useState('5 seconds');
    const [isGenerating, setIsGenerating] = useState(false);

    const [assets, setAssets] = useState([]);
    const [isLoadingAssets, setIsLoadingAssets] = useState(false);
    const [playingTrackId, setPlayingTrackId] = useState(null);
    const [loadingTrackId, setLoadingTrackId] = useState(null);
    const audioRef = React.useRef(null);

    React.useEffect(() => {
        audioRef.current = new Audio();
        audioRef.current.onended = () => {
            setPlayingTrackId(null);
            setLoadingTrackId(null);
        };
        audioRef.current.onplaying = () => setLoadingTrackId(null);
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, []);

    const togglePlay = (e, track) => {
        e.stopPropagation();
        if (playingTrackId === track._id || loadingTrackId === track._id) {
            audioRef.current.pause();
            setPlayingTrackId(null);
            setLoadingTrackId(null);
        } else {
            if (audioRef.current) {
                setLoadingTrackId(track._id);
                setPlayingTrackId(track._id);
                audioRef.current.src = track.url;
                audioRef.current.play().catch(err => {
                    console.error("Playback failed:", err);
                    setPlayingTrackId(null);
                    setLoadingTrackId(null);
                });
            }
        }
    };

    React.useEffect(() => {
        // Sound effects API has been moved to AssetsLibraryPage
        setAssets([]);
        setIsLoadingAssets(false);
    }, [isOpen]);

    if (!isOpen) return null;

    const durations = ['2 seconds', '5 seconds', '10 seconds', 'Custom'];

    const handleGenerate = async () => {
        if (!prompt) return;
        setIsGenerating(true);
        try {
            const durationSec = parseInt(duration);
            const payload = {
                text: prompt,
                duration: durationSec
            };
            const response = await services.generateSoundEffect(payload);
            if (onGenerate) {
                onGenerate(response.data);
                onClose();
            }
        } catch (error) {
            console.error("Error generating sound effect:", error);
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="bg-white dark:bg-[#111111] w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-white/10">
                {/* Header */}
                <div className="flex items-center justify-between px-8 py-6">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">Create Sound Effect</h1>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 px-8 pb-8 space-y-12">
                    {/* Step 1 */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-4 text-gray-400">
                            <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center text-sm font-bold">1</div>
                            <h2 className="text-sm font-semibold uppercase tracking-wider bg-white dark:bg-[#111111] pr-4">Enter your prompt to create a sound effect</h2>
                            <div className="flex-1 h-[1px] bg-gray-100 dark:bg-white/5"></div>
                        </div>

                        <textarea
                            className="w-full bg-white dark:bg-black border border-gray-200 dark:border-white/10 rounded-xl p-6 text-base min-h-[160px] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-gray-900 dark:text-white"
                            placeholder="Describe the sound effect you want to generate (e.g., 'Thunder storm with rain in the background')"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                        />
                    </div>

                    {/* Step 2 */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-4 text-gray-400">
                            <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center text-sm font-bold">2</div>
                            <h2 className="text-sm font-semibold uppercase tracking-wider bg-white dark:bg-[#111111] pr-4">Basic Configuration</h2>
                            <div className="flex-1 h-[1px] bg-gray-100 dark:bg-white/5"></div>
                        </div>

                        <div className="space-y-8">
                            {/* Enhance Toggle */}
                            <div className="flex items-center gap-4">
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Enhance prompt with AI</span>
                                <button
                                    onClick={() => setEnhanceWithAi(!enhanceWithAi)}
                                    className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${enhanceWithAi ? 'bg-orange-500' : 'bg-gray-200 dark:bg-gray-800'}`}
                                >
                                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${enhanceWithAi ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                </button>
                            </div>

                            {/* Duration selection */}
                            <div className="flex items-center gap-6">
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Duration</span>
                                <div className="flex flex-wrap gap-3">
                                    {durations.map((d) => (
                                        <button
                                            key={d}
                                            onClick={() => setDuration(d)}
                                            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${duration === d
                                                ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-500 text-orange-600 dark:text-orange-400'
                                                : 'bg-white dark:bg-[#191919] border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                                                }`}
                                        >
                                            {d}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Generate Button Container */}
                    <div className="pt-4 flex justify-end border-b border-gray-100 dark:border-white/5 pb-8">
                        <button
                            onClick={handleGenerate}
                            disabled={isGenerating}
                            className={`
                                relative overflow-hidden group px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold transition-all shadow-lg shadow-orange-500/25 flex items-center gap-2
                                ${isGenerating ? 'opacity-80 cursor-not-allowed' : 'active:scale-95'}
                            `}
                        >
                            {isGenerating ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span>Generating...</span>
                                </>
                            ) : (
                                <span>Generate Sound Effect</span>
                            )}
                        </button>
                    </div>

                    {/* Add From Media Library */}
                    <div className="space-y-4">
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Add From Media Library</h2>
                        <button
                            onClick={onAddFromDrive}
                            className="w-48 bg-gray-50 dark:bg-[#191919] border border-gray-100 dark:border-white/5 rounded-2xl p-6 flex flex-col items-start gap-4 hover:shadow-lg hover:border-orange-500/20 transition-all group"
                        >
                            <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                                <Cloud className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 group-hover:text-orange-500">Add From Media Library</span>
                        </button>
                    </div>

                    {/* Sound Effects Library */}
                    <div className="space-y-6">
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Sound Effects Library</h2>

                        {/* Tracks Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {isLoadingAssets ? (
                                <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
                                    <Loader2 className="w-8 h-8 animate-spin mb-4" />
                                    <p>Loading library assets...</p>
                                </div>
                            ) : assets.length === 0 ? (
                                <div className="col-span-full text-center py-12 text-gray-500 bg-gray-50 dark:bg-white/5 rounded-2xl border border-dashed border-gray-200 dark:border-white/10">
                                    No sound effects found in your library.
                                </div>
                            ) : (
                                assets.map(track => (
                                    <div
                                        key={track._id}
                                        className={`group bg-gray-50 dark:bg-[#191919] border ${playingTrackId === track._id ? 'border-orange-500 shadow-md' : 'border-gray-100 dark:border-white/5'} rounded-2xl p-4 flex items-center gap-4 hover:shadow-lg hover:border-orange-500/20 transition-all cursor-pointer`}
                                        onClick={() => {
                                            if (onSelectTrack) {
                                                onSelectTrack({
                                                    id: track._id,
                                                    name: track.name,
                                                    url: track.url,
                                                    type: track.mimeType || 'audio/mpeg',
                                                    duration: track.duration || 5
                                                });
                                            }
                                            if (onClose) onClose();
                                        }}
                                    >
                                        <button
                                            onClick={(e) => togglePlay(e, track)}
                                            className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg transition-transform ${playingTrackId === track._id ? 'bg-orange-600 scale-105 shadow-orange-500/30' : 'bg-orange-500 shadow-orange-500/10 group-hover:scale-105'}`}
                                        >
                                            {loadingTrackId === track._id ? (
                                                <Loader2 className="w-4 h-4 text-white animate-spin" />
                                            ) : playingTrackId === track._id ? (
                                                <Pause className="w-4 h-4 text-white fill-white" />
                                            ) : (
                                                <Play className="w-4 h-4 text-white fill-white" />
                                            )}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-white truncate cursor-pointer" title={track.name}>{track.name}</div>
                                            <div className="text-xs text-gray-500 truncate">{track.mimeType}</div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSelectTrack && onSelectTrack({
                                                    id: track._id,
                                                    name: track.name,
                                                    url: track.url,
                                                    type: track.mimeType || 'audio/mpeg',
                                                    duration: track.duration || 5
                                                });
                                                onClose();
                                            }}
                                            className="px-3 py-1.5 bg-orange-50 text-orange-600 hover:bg-orange-100 dark:bg-orange-500/10 dark:hover:bg-orange-500/20 rounded-lg text-xs font-semibold transition-colors opacity-0 group-hover:opacity-100"
                                        >
                                            Add
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SoundEffectModal;
